import React from 'react';
import { Button, Col, Form, Row } from '../../../components/Bootstrap';
import { Switch } from '../../../components';
import { MenuItem, MenuOption, MenuOptionGroup, VenueTax } from '../types';
import './DrinkEditor.scss';
import { DrinkEditorInputRow } from './DrinkEditorInputRow';
import { DrinkEditorRow } from './DrinkEditorRow';
import { OptionGroupEditor } from './OptionGroupEditor';
import { PhonePreview } from './PhonePreview';
import { getSortingFunction, isCarouselConfig } from './utils';
import { MenuItemInput, MenuOptionGroupInput, MenuOptionInput } from './../../../globalTypes';
import { UploadDropzone } from '../../../components';
import { FormMenuItem } from './types';
import { TaxEditor } from './TaxEditor';

const classNames = require('classnames');

export interface DrinkEditorProps {
  categoryId: number;
  menuItem: MenuItem;
  // options: {
  //   [type: string]: Option[];
  // };
  showTaxableSwitch: boolean;
  onSubmit(data: MenuItemInput): void;
  onCancel(): void;

  taxes?: VenueTax[];

  optionLookUpVariables: {
    venueId: string;
    menuId: string;
    categoryId: string;
    itemId?: string;
  };
}

interface DrinkEditorState {
  menuItem: FormMenuItem;
  optionType: {
    [type: string]: boolean;
  };
  imageSizeError: boolean;
  imageExtensionError: boolean;
  newImage?: {
    file: File;
    previewUrl: string;
  };
}

export class ItemEditor extends React.Component<DrinkEditorProps, DrinkEditorState> {
  public handlePriceChange: (e) => void;
  public handleDescriptionChange: (e) => void;
  public handleNameChange: (e) => void;

  constructor(props: DrinkEditorProps) {
    super(props);

    const { menuItem } = props;

    const optionType = menuItem.optionGroups.reduce((accum, optionGroup) => {
      const optionConfig = optionGroup.editorConfig;
      if (isCarouselConfig(optionConfig)) {
        accum[optionGroup.id] = true;
      } else {
        const forType = optionGroup.menuOptions;
        accum[optionGroup.id] = forType.length > 0 || !optionConfig.canTurnOff;
      }

      return accum;
    }, {});

    this.state = {
      menuItem: {
        ...menuItem,
        price: `${menuItem.price}`,
      },
      optionType,
      imageSizeError: false,
      imageExtensionError: false,
    };

    this.handlePriceChange = this.updateMenuItemProperty.bind(this, 'price');
    this.handleDescriptionChange = this.updateItemProperty.bind(this, 'description');
    this.handleNameChange = this.updateItemProperty.bind(this, 'name');
  }

  getOptionGroup = (optionGroupId: string) => {
    return this.state.menuItem.optionGroups.find((optionGroup) => optionGroup.id === optionGroupId);
  };

  addOptions = ({ optionGroupId, options }: { optionGroupId: string; options: MenuOption[] }) => {
    const optionGroup = this.getOptionGroup(optionGroupId);
    const newList = optionGroup.menuOptions.concat(options);

    const categoryConfig = optionGroup.editorConfig;
    const sorter = getSortingFunction(categoryConfig.sorter);
    if (sorter) {
      newList.sort(sorter);
    }

    this.updateOptionType(optionGroupId, newList);
  };

  updateOption = ({
    optionGroupId,
    oldOption,
    newOption,
  }: {
    optionGroupId: string;
    oldOption: MenuOption;
    newOption: MenuOption;
  }) => {
    const optionGroup = this.getOptionGroup(optionGroupId);

    const index = optionGroup.menuOptions.indexOf(oldOption);
    const newList = [...optionGroup.menuOptions];
    newList[index] = newOption;

    this.updateOptionType(optionGroupId, newList);
  };

  moveOption = (optionGroupId: string, dragIndex: number, hoverIndex: number) => {
    const optionGroup = this.getOptionGroup(optionGroupId);

    const dragCard = optionGroup.menuOptions[dragIndex];

    const newList: MenuOption[] = [...optionGroup.menuOptions];
    newList.splice(dragIndex, 1);
    newList.splice(hoverIndex, 0, dragCard);

    this.updateOptionType(optionGroupId, newList);
  };

  deleteOption = ({ optionGroupId, option }: { optionGroupId: string; option: MenuOption }) => {
    const optionGroup = this.getOptionGroup(optionGroupId);
    const updated = optionGroup.menuOptions.filter((o) => o !== option);

    this.updateOptionType(optionGroupId, updated);
  };

  updateOptionType = (optionGroupId: string, values: MenuOption[]) => {
    const optionGroups = this.state.menuItem.optionGroups.map((optionGroup): MenuOptionGroup => {
      if (optionGroup.id === optionGroupId) {
        return {
          ...optionGroup,
          menuOptions: values,
        };
      }

      return optionGroup;
    });

    const menuItem: FormMenuItem = {
      ...this.state.menuItem,
      optionGroups,
    };

    this.setState({ menuItem });
  };

  updateMenuItemProperty = <Key extends keyof FormMenuItem>(key: Key, value: FormMenuItem[Key]) => {
    const menuItem: FormMenuItem = {
      ...this.state.menuItem,
      [key]: value,
    };

    this.setState({ menuItem });
  };

  updateItemProperty = <Key extends keyof FormMenuItem['item']>(key: Key, value: FormMenuItem['item'][Key]) => {
    const menuItem: FormMenuItem = {
      ...this.state.menuItem,
      item: {
        ...this.state.menuItem.item,
        [key]: value,
      },
    };

    this.setState({ menuItem });
  };

  handleAlcoholicSwitch = () => {
    this.updateItemProperty('alcoholServings', this.state.menuItem.item.alcoholServings === 0 ? 1 : 0);
  };

  handleTaxableSwitch = () => {
    this.updateMenuItemProperty('taxable', !this.state.menuItem.taxable);
  };

  onRemoveItemTax = (tax: VenueTax) => {
    this.updateItemProperty(
      'taxes',
      this.state.menuItem.item.taxes.filter((t) => t.id !== tax.id)
    );
  };

  onAddTax = (tax: VenueTax) => {
    this.updateItemProperty('taxes', this.state.menuItem.item.taxes.concat(tax));
  };

  handleImageUpload = async (file: File) => {
    this.setState({
      newImage: {
        file,
        previewUrl: URL.createObjectURL(file),
      },
    });
  };

  handleSubmit = async (e) => {
    e.preventDefault();

    let price = this.state.menuItem.price as any;

    if (price && typeof price === 'string') {
      price = parseFloat(price.replace('$', ''));
    }

    const optionGroups: MenuOptionGroupInput[] = this.state.menuItem.optionGroups
      .filter((optionGroup) => {
        return this.state.optionType[optionGroup.id];
      })
      .map((optionGroup) => {
        return {
          id: optionGroup.id,
          options: optionGroup.menuOptions.map((menuOption): MenuOptionInput => {
            return {
              id: menuOption.id ? menuOption.id : undefined,
              price: menuOption.price,
              enabled: menuOption.enabled,
              option: {
                id: menuOption.option.id ? menuOption.option.id : undefined,
                name: menuOption.option.name,
              },
            };
          }),
        };
      });

    const submitData: MenuItemInput = {
      price,
      countsTowardMinimum: this.state.menuItem.item.alcoholServings > 0,
      taxable: this.state.menuItem.taxable,
      item: {
        name: this.state.menuItem.item.name,
        alcoholServings: this.state.menuItem.item.alcoholServings,
        additionalComments: true,
        description: this.state.menuItem.item.description || '',
        image: this.state.newImage ? this.state.newImage.file : undefined,
        taxIds: this.state.menuItem.item.taxes.map((t) => t.id),
      },
      optionGroups,
    };

    this.props.onSubmit(submitData);
  };

  toggleOptionType = (optionGroupId: string) => {
    const newState = !this.state.optionType[optionGroupId];

    if (newState && this.getOptionGroup(optionGroupId).editorConfig.defaultOptions) {
      const optionGroups: MenuOptionGroup[] = this.state.menuItem.optionGroups.map((optionGroup) => {
        if (optionGroup.id === optionGroupId) {
          return {
            ...optionGroup,
            menuOptions: this.getOptionGroup(optionGroupId).editorConfig.defaultOptions,
          };
        }

        return optionGroup;
      });
      this.setState({
        optionType: {
          ...this.state.optionType,
          [optionGroupId]: newState,
        },
        menuItem: {
          ...this.state.menuItem,
          optionGroups,
        },
      });
    } else {
      this.setState({
        optionType: {
          ...this.state.optionType,
          [optionGroupId]: newState,
        },
      });
    }
  };

  onSizeError = () => {
    this.setState({ imageSizeError: true });
  };

  onExtensionError = () => {
    this.setState({ imageExtensionError: true });
  };

  render() {
    const { onCancel } = this.props;
    const { menuItem } = this.state;
    const { item } = menuItem;
    const { onSizeError } = this;

    const imageRestrictionClasses = classNames('drink-editor-image-restrictions', {
      'drink-editor-image-restrictions--error': this.state.imageSizeError,
    });

    const isPOSItem = !!this.props.menuItem.item.posInfo;

    return (
      <div className='drink-editor-card-block'>
        <Form onSubmit={this.handleSubmit}>
          <Row>
            <Col className='d-none d-md-block' md={4}>
              <PhonePreview
                menuItem={menuItem}
                imageUrl={this.state.newImage ? this.state.newImage.previewUrl : undefined}
              />
            </Col>
            <Col>
              <div className='drink-editor-fields'>
                <h1 className='drink-editor-top-heading'>Item Details</h1>
                <DrinkEditorInputRow
                  label='Name'
                  required={true}
                  type='text'
                  name='name'
                  value={item.name}
                  onChange={this.handleNameChange}
                  disabled={item.shared}
                  className='drink-editor-name'
                />
                <DrinkEditorInputRow
                  label='Base Price'
                  required={true}
                  type='text'
                  name='price'
                  value={`${menuItem.price}`}
                  onChange={this.handlePriceChange}
                  className='drink-editor-price'
                  disabled={isPOSItem}
                />
                <DrinkEditorRow label='Alcoholic'>
                  <Switch
                    id='alcoholic-switch'
                    checked={item.alcoholServings === 1}
                    onChange={this.handleAlcoholicSwitch}
                    size='sm'
                    disabled={item.shared}
                  />
                </DrinkEditorRow>
                {this.props.showTaxableSwitch && !isPOSItem && (
                  <DrinkEditorRow label='Taxable'>
                    <Switch
                      id='taxable-switch'
                      checked={menuItem.taxable}
                      onChange={this.handleTaxableSwitch}
                      size='sm'
                    />
                  </DrinkEditorRow>
                )}
                <TaxEditor
                  venueTaxes={this.props.taxes}
                  itemTaxes={item.taxes}
                  onRemoveItemTax={this.onRemoveItemTax}
                  onAddTax={this.onAddTax}
                />
                <DrinkEditorInputRow
                  label='Description'
                  required={false}
                  type='textarea'
                  name='description'
                  value={item.description || ''}
                  onChange={this.handleDescriptionChange}
                  disabled={item.shared}
                />
                {menuItem.optionGroups.map((optionGroup) => (
                  <OptionGroupEditor
                    key={optionGroup.id}
                    addOptions={this.addOptions}
                    updateOption={this.updateOption}
                    deleteOption={this.deleteOption}
                    toggleOptionType={this.toggleOptionType}
                    optionType={this.state.optionType}
                    moveOption={this.moveOption}
                    optionLookUpVariables={this.props.optionLookUpVariables}
                    isPOSItem={!!item.posInfo}
                    optionGroup={optionGroup}
                  />
                ))}
                {!item.shared && (
                  <div>
                    <h3 className='drink-editor-heading'>Upload Photo</h3>
                    <div className={imageRestrictionClasses}>
                      Images must be less than 600 KB. Recommended size: 750px x 386px
                    </div>
                    {this.state.imageExtensionError && <div>The file you uploaded is not an image</div>}
                    <UploadDropzone
                      onSizeError={onSizeError}
                      maxSize={600}
                      onChange={this.handleImageUpload}
                      aspectRatio={16 / 9}
                      onExtensionError={this.onExtensionError}
                    >
                      <Button type='button' color='outline-primary' className='btn--rounded-padded'>
                        Upload
                      </Button>
                    </UploadDropzone>
                  </div>
                )}
                <div className='drink-editor-buttons'>
                  <Button type='button' color='link' onClick={onCancel}>
                    Cancel
                  </Button>{' '}
                  <Button type='submit' color='primary' className='btn--rounded-padded'>
                    Save
                  </Button>
                </div>
              </div>
            </Col>
          </Row>
        </Form>
      </div>
    );
  }
}
